import { PayloadAction, createSlice } from '@reduxjs/toolkit';

export interface IAgencyAllocatorConfig {
  pacingRatio?: string;
  dialPad: boolean;
}

export interface IAgencyAllocatorConfigInitialState
  extends IAgencyAllocatorConfig {
  isLoading: boolean;
  isLoaded: boolean;
  error: string | null;
}

export const AGENCY_ALLOCATOR_CONFIG_SESSION_KEY = 'agencyAllocatorConfig';

const readFromSession = (): IAgencyAllocatorConfig | null => {
  try {
    const raw = sessionStorage.getItem(AGENCY_ALLOCATOR_CONFIG_SESSION_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (typeof parsed !== 'object' || parsed === null) return null;
    return {
      pacingRatio:
        typeof parsed.pacingRatio === 'string' ? parsed.pacingRatio : undefined,
      dialPad: parsed.dialPad === true,
    };
  } catch {
    return null;
  }
};

const persisted = readFromSession();

export const agencyAllocatorConfigInitialState: IAgencyAllocatorConfigInitialState =
  {
    pacingRatio: persisted?.pacingRatio,
    dialPad: persisted?.dialPad ?? false,
    isLoading: false,
    isLoaded: persisted !== null,
    error: null,
  };

const agencyAllocatorConfigSlice = createSlice({
  name: 'agencyAllocatorConfig',
  initialState: agencyAllocatorConfigInitialState,
  reducers: {
    fetchAgencyAllocatorConfig: (state) => {
      state.isLoading = true;
      state.error = null;
    },
    fetchAgencyAllocatorConfigSuccess: (
      state,
      { payload }: PayloadAction<IAgencyAllocatorConfig>
    ) => {
      state.pacingRatio = payload.pacingRatio;
      state.dialPad = payload.dialPad;
      state.isLoading = false;
      state.isLoaded = true;
      state.error = null;
    },
    fetchAgencyAllocatorConfigFailure: (
      state,
      { payload }: PayloadAction<string>
    ) => {
      state.pacingRatio = undefined;
      state.dialPad = false;
      state.isLoading = false;
      state.isLoaded = true;
      state.error = payload;
    },
    resetAgencyAllocatorConfig: () => ({
      ...agencyAllocatorConfigInitialState,
      pacingRatio: undefined,
      dialPad: false,
      isLoaded: false,
      isLoading: false,
      error: null,
    }),
  },
});

export const {
  fetchAgencyAllocatorConfig,
  fetchAgencyAllocatorConfigSuccess,
  fetchAgencyAllocatorConfigFailure,
  resetAgencyAllocatorConfig,
} = agencyAllocatorConfigSlice.actions;

export default agencyAllocatorConfigSlice.reducer;
